import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import { alpha } from '@mui/material';
import { buildPureLightTheme } from './schemes/PureLightTheme';
import { DarkTheme } from './schemes/DarkTheme';
import { NEW_LOOK_DARK, NEW_LOOK_LIGHT, type NewLookTokens } from './newLookTokens';

// =====================================================================================
// The back-office "new look" as ONE theme layer, so every screen matches the redesigned
// pages (Settings, Branches, Billing …) without touching each page: dark primary buttons,
// quiet outlined secondary buttons, 8px corners, no ALL-CAPS, flat hairline cards, calm
// inputs with a blue focus, underline tabs, blue 40×22 toggles, flat tables.
//
// Built over the classic schemes with createTheme(base, overrides) — the same pattern as
// ModernTheme. The cashier POS screens (sale / touch terminal / return / exchange) keep the
// original theme through <PosThemeScope> (theme/PosThemeScope.tsx); the company "Full
// Modern" look is its own design and is not changed.
// =====================================================================================

const LIGHT_PRIMARY = '#2A78D6';

/** Deep-merged OVER a full classic theme, so only the parts that change are given here. */
function overrides(k: NewLookTokens, dark: boolean): Partial<ThemeOptions> {
  const accent = k.accent;
  const danger = dark ? '#F04438' : '#D92D20';
  const primaryHover = dark ? '#FFFFFF' : '#2B2A27';
  const quiet = {
    color: k.text,
    borderColor: k.searchBorder,
    backgroundColor: k.pageBg,
    '&:hover': { backgroundColor: k.hover, borderColor: k.searchBorder },
  };

  return {
    palette: {
      ...(dark
        ? { primary: { main: accent, light: '#6AA6EA', dark: '#2A6CB8', contrastText: '#FFFFFF' } }
        : {}),
      // One page colour across the whole back office (white light, warm near-black dark).
      background: dark ? { default: k.pageBg } : { default: k.pageBg, paper: '#FFFFFF' },
    },
    typography: {
      // Small labels read as sentences, not SHOUTING (the classic scheme upper-cased captions).
      caption: { textTransform: 'none' },
    },
    // App chrome like Claude / ChatGPT: a soft off-white sidebar with a hairline edge, a flat
    // white header, white pages. (Custom theme fields read by layouts/SidebarLayout.)
    ...({
      sidebar: dark
        ? {
            background: k.navBg,
            textColor: k.text,
            dividerBg: k.border,
            menuItemColor: k.navText,
            menuItemColorActive: k.text,
            menuItemBg: 'transparent',
            menuItemBgActive: k.selected,
            menuItemIconColor: k.navText,
            menuItemIconColorActive: k.text,
            menuItemHeadingColor: k.muted,
          }
        : {
            background: '#F9F9F8',
            textColor: k.text,
            dividerBg: k.border,
            menuItemColor: k.navText,
            menuItemColorActive: k.text,
            menuItemBg: 'transparent',
            menuItemBgActive: '#EBEBEA',
            menuItemIconColor: k.navText,
            menuItemIconColorActive: k.text,
            menuItemHeadingColor: k.muted,
            scrollbarColor: '#CFCFCF',
          },
      header: {
        background: k.pageBg,
        boxShadow: 'none',
        textColor: k.text,
      },
    } as Record<string, unknown>),
    shape: { borderRadius: 8 },
    components: {
      MuiButtonBase: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: 8,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            '&:active': { boxShadow: 'none' },
          },
          containedPrimary: {
            backgroundColor: k.buttonBg,
            color: k.buttonText,
            '&:hover': { backgroundColor: primaryHover },
            '&.Mui-disabled': { backgroundColor: k.buttonBg, color: k.buttonText, opacity: 0.4 },
          },
          outlinedPrimary: quiet,
          outlinedSecondary: quiet,
          outlinedInherit: quiet,
          textPrimary: {
            color: accent,
            '&:hover': { backgroundColor: alpha(accent, 0.08) },
          },
          sizeSmall: { padding: '5px 12px', fontSize: 13.5, lineHeight: 1.5 },
          sizeMedium: { padding: '7px 16px', fontSize: 14 },
          sizeLarge: { padding: '10px 22px', fontSize: 15 },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: k.navText,
            background: k.pageBg,
            borderColor: k.searchBorder,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { color: k.text, background: k.hover },
            '&.Mui-selected, &.Mui-selected:hover': { color: k.buttonText, background: k.buttonBg },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: k.searchBorder },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: k.muted },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: accent,
              borderWidth: 1,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline, &.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: danger },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline, &.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: k.rowDivider,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { '&.Mui-focused': { color: accent }, '&.Mui-error': { color: danger } },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { boxShadow: 'none', border: `1px solid ${k.border}`, borderRadius: 12, backgroundImage: 'none' },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          title: { fontSize: 16, fontWeight: 600 },
          subheader: { fontSize: 14, color: k.muted },
        },
      },
      MuiPaper: {
        styleOverrides: {
          outlined: { boxShadow: 'none', borderColor: k.border },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 12, border: `1px solid ${k.border}`, boxShadow: '0 20px 48px rgba(0,0,0,0.18)' },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontSize: 16, fontWeight: 600 } },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 10, border: `1px solid ${k.border}`, boxShadow: '0 12px 32px rgba(0,0,0,0.12)' },
        },
      },
      MuiPopover: {
        styleOverrides: { paper: { borderRadius: 10 } },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 8, fontWeight: 500 } },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: dark ? '#F2F1EE' : '#141413',
            color: dark ? '#141413' : '#FFFFFF',
            fontSize: 12.5,
            fontWeight: 500,
            borderRadius: 6,
            padding: '6px 10px',
          },
          arrow: { color: dark ? '#F2F1EE' : '#141413' },
        },
      },
      MuiLink: {
        styleOverrides: { root: { color: accent } },
      },
      // Toggles: the 40 × 22 blue switch of the redesigned pages, everywhere.
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 40,
            height: 22,
            padding: 0,
            overflow: 'visible',
            margin: 0,
            '.MuiFormControlLabel-root > &': { marginLeft: 11, marginRight: 10 },
            '& .MuiButtonBase-root, & .MuiSwitch-switchBase': {
              position: 'absolute',
              padding: 3,
              margin: 0,
              color: '#FFFFFF',
              transitionDuration: '160ms',
              '&:hover': { backgroundColor: 'transparent' },
            },
            '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(18px)', color: '#FFFFFF' },
            // The hidden input is 300% of the thumb by default; with overflow visible it poked past
            // the switch (phone pages scrolled sideways). Make it cover exactly the 40px switch.
            '& .MuiSwitch-switchBase .MuiSwitch-input': { left: 0, width: 40 },
            '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-input': { left: -18 },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accent, opacity: 1 },
            '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb, & .MuiSwitch-thumb': { backgroundColor: '#FFFFFF' },
            '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { opacity: 0.45 },
            '& .MuiSwitch-switchBase.Mui-focusVisible .MuiSwitch-thumb': { outline: `2px solid ${accent}`, outlineOffset: 2 },
          },
          sizeSmall: {
            width: 32,
            height: 18,
            '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(14px)' },
            '& .MuiSwitch-switchBase .MuiSwitch-input': { width: 32 },
            '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-input': { left: -14 },
            '& .MuiSwitch-thumb': { width: 12, height: 12 },
          },
          thumb: { width: 16, height: 16, border: 0, boxShadow: '0 1px 2px rgba(0,0,0,0.25)' },
          track: { borderRadius: 11, backgroundColor: k.toggleOff, opacity: 1, border: 0, boxShadow: 'none' },
          colorPrimary: { '&.Mui-checked .MuiSwitch-thumb': { backgroundColor: '#FFFFFF' } },
        },
      },
      // Underline tabs instead of the classic pill tabs.
      MuiTabs: {
        styleOverrides: {
          root: {
            height: 'auto',
            minHeight: 42,
            overflow: 'visible',
            // The classic scheme forces `overflow: visible !important` on the scroller (for its pill
            // indicator's shadow), which stops `variant="scrollable"` tab bars from scrolling — on
            // phones they widened the whole page instead. Underline tabs need no overflow: scroll.
            '& .MuiTabs-scrollableX': { overflowX: 'auto !important', overflowY: 'hidden !important' },
          },
          indicator: {
            height: 2,
            minHeight: 2,
            borderRadius: 0,
            border: 0,
            boxShadow: 'none',
            backgroundColor: k.text,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            height: 'auto',
            minHeight: 42,
            borderRadius: 0,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 14,
            color: k.navText,
            '&.MuiButtonBase-root': { paddingLeft: 12, paddingRight: 12, marginRight: 4 },
            '&.Mui-selected, &.Mui-selected:hover': { color: k.text, zIndex: 'auto' },
            '&:hover': { color: k.text },
          },
        },
      },
      // Flat tables: grey header band without upper-case, hairline rows.
      MuiTableRow: {
        styleOverrides: {
          head: { background: k.subtleBg },
          root: { '&.MuiTableRow-hover:hover': { backgroundColor: k.subtleBg } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: k.rowDivider },
          head: { textTransform: 'none', fontSize: 13, fontWeight: 500, color: k.muted, borderBottomColor: k.border },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: { '&:before': { display: 'none' } },
        },
      },
    },
  };
}

const cache = new Map<string, Theme>();

/** The back-office theme for a classic scheme name ('PureLightTheme' | 'DarkTheme'). */
export function newLookThemeFor(themeName: string): Theme {
  const dark = themeName === 'DarkTheme';
  const key = dark ? 'dark' : 'light';
  let t = cache.get(key);
  if (!t) {
    t = dark
      ? createTheme(DarkTheme, overrides(NEW_LOOK_DARK, true))
      : createTheme(buildPureLightTheme(LIGHT_PRIMARY), overrides(NEW_LOOK_LIGHT, false));
    cache.set(key, t);
  }
  return t;
}
